import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, StatusBadge, EmptyState } from '../../components/common/PageHeader';
import api, { API_BASE } from '../../lib/api';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Briefcase, ExternalLink, FileText, Receipt, Calendar as CalendarIcon, CalendarClock, Check, X } from 'lucide-react';
import { statusLabel, serviceTypeLabel, meetingToolLabel } from '../../lib/constants';
import { toast } from 'sonner';

const CustomerJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(null); // job_id when reject dialog open
  const [rejectMessage, setRejectMessage] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get('/jobs').then(r => setJobs(r.data));
  useEffect(() => { load(); }, []);

  // Show a friendly notice when Stripe cancel-URL redirects back here
  useEffect(() => {
    if (searchParams.get('payment') === 'cancelled') {
      toast.message('Zahlung abgebrochen — Sie können die Bezahlung jederzeit über den „Jetzt bezahlen"-Button fortsetzen.', { duration: 6000 });
      // Clear the query param so the toast doesn't show again on refresh
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      next.delete('job_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const acceptProposal = async (jobId, proposalId) => {
    try {
      await api.post(`/jobs/${jobId}/proposal/respond`, { action: 'accept', accepted_proposal_id: proposalId });
      toast.success('Termin angenommen - das Team wird einen Dolmetscher zuweisen.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    }
  };

  const rejectAllProposals = async () => {
    try {
      await api.post(`/jobs/${rejectOpen}/proposal/respond`, { action: 'reject', message: rejectMessage });
      toast.success('Rückmeldung gesendet');
      setRejectOpen(null);
      setRejectMessage('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    }
  };

  const downloadWithAuth = async (e, url, filename) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('dd_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error('Download fehlgeschlagen');
    }
  };

  const pendingProposals = jobs.filter(j => j.status === 'gegenvorschlag_gesendet' && j.proposals?.length > 0);

  return (
    <AppLayout>
      <PageHeader title="Meine Aufträge" description="Alle gebuchten und vergangenen Termine">
        <Link to="/customer/book" data-testid="new-booking-btn" className="bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Neuer Termin
        </Link>
      </PageHeader>

      {/* Pending proposals */}
      {pendingProposals.length > 0 && (
        <div className="mb-6 space-y-4">
          {pendingProposals.map(j => (
            <div key={j.id} data-testid={`proposal-card-${j.id}`} className="bg-amber-50 border border-amber-300 rounded-md p-5">
              <div className="flex items-start gap-3 mb-3">
                <CalendarClock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-heading text-base font-semibold text-amber-900">
                    Terminvorschläge zu Ihrer Anfrage {j.job_number || j.id.slice(0, 12)}
                  </h3>
                  <p className="text-sm text-amber-800 mt-1">
                    Ihr ursprünglicher Wunschtermin ({j.date} um {j.time}) ist nicht verfügbar. Bitte wählen Sie einen der folgenden Vorschläge:
                  </p>
                </div>
              </div>
              <div className="space-y-2 mt-4">
                {j.proposals.map((p, i) => (
                  <div key={p.id} className="bg-white border border-amber-200 rounded p-3 flex items-center justify-between gap-3">
                    <div className="text-sm">
                      <div className="font-medium text-slate-900">Vorschlag {i+1}: {p.date} um {p.time}</div>
                      <div className="text-xs text-slate-500">{p.duration} Min{p.note ? ` · ${p.note}` : ''}</div>
                    </div>
                    <button
                      onClick={() => acceptProposal(j.id, p.id)}
                      data-testid={`accept-proposal-${j.id}-${i}`}
                      className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded px-3 py-1.5 text-xs"
                    >
                      <Check className="w-3.5 h-3.5" /> Annehmen
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setRejectOpen(j.id)}
                data-testid={`reject-all-${j.id}`}
                className="mt-3 text-sm text-amber-800 hover:text-amber-900 inline-flex items-center gap-1.5"
              >
                <X className="w-4 h-4" /> Keiner der Termine passt mir
              </button>
            </div>
          ))}
        </div>
      )}

      {jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="Noch keine Aufträge" description="Buchen Sie Ihren ersten Dolmetschtermin." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md card-subtle overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Datum</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Sprache</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Einsatzart</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Dauer</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Status</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Meeting</th>
                <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Dokumente</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="hover:bg-slate-50" data-testid={`customer-job-${j.id}`}>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-900">
                    <div>{j.date} {j.time}</div>
                    <div className="text-xs text-slate-400 font-mono">{j.job_number || j.id.slice(0, 12)}</div>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{j.source_language} → {j.target_language}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{serviceTypeLabel(j.service_type)}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">
                    {j.billed_duration_minutes ? (
                      <div>
                        <span className="font-mono">{j.billed_duration_minutes} Min</span>
                        <div className="text-xs text-slate-400">abgerechnet</div>
                      </div>
                    ) : `${j.duration} Min.`}
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge status={j.status} label={statusLabel(j.status)} />
                      {j.payment_status === 'paid' && (
                        <span data-testid={`paid-badge-${j.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5">
                          Bezahlt
                        </span>
                      )}
                      {j.payment_status === 'pending' && (
                        <button
                          onClick={async () => {
                            try {
                              const r = await api.post('/payments/checkout/session', { job_id: j.id });
                              if (r.data?.url) window.location.href = r.data.url;
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Zahlung konnte nicht gestartet werden');
                            }
                          }}
                          data-testid={`pay-now-${j.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 rounded-full px-1.5 py-0.5 transition-colors"
                          title="Zahlung jetzt abschließen"
                        >
                          Jetzt bezahlen
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4">
                    {j.meeting_link ? (
                      <a href={j.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded px-2 py-1" data-testid={`meeting-link-${j.id}`}>
                        <ExternalLink className="w-3 h-3" /> {({google_meet:'Google Meet',microsoft_teams:'Teams',zoom:'Zoom',manual:'Link'})[j.meeting_provider] || meetingToolLabel(j.meeting_tool) || 'Öffnen'}
                      </a>
                    ) : <span className="text-xs text-slate-400">-</span>}
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={(e) => downloadWithAuth(e, `${API_BASE}/jobs/${j.id}/pdf/confirmation`, `Auftragsbestaetigung_${(j.job_number || j.id).replace(/[^a-z0-9\-]/gi,'_')}.pdf`)}
                        title="Auftragsbestätigung als PDF"
                        data-testid={`pdf-conf-${j.id}`}
                        className="text-slate-500 hover:text-[#0F4C81] p-1 rounded hover:bg-slate-100"
                      ><FileText className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={(e) => downloadWithAuth(e, `${API_BASE}/jobs/${j.id}/pdf/invoice`, `Rechnung_${(j.job_number || j.id).replace(/[^a-z0-9\-]/gi,'_')}.pdf`)}
                        title="Rechnung als PDF"
                        data-testid={`pdf-inv-${j.id}`}
                        className="text-slate-500 hover:text-[#0F4C81] p-1 rounded hover:bg-slate-100"
                      ><Receipt className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={(e) => downloadWithAuth(e, `${API_BASE}/jobs/${j.id}/calendar.ics`, `dolmetschtermin_${(j.job_number || j.id).replace(/[^a-z0-9\-]/gi,'_')}.ics`)}
                        title="Zum Kalender hinzufügen (.ics)"
                        data-testid={`ics-${j.id}`}
                        className="text-slate-500 hover:text-[#0F4C81] p-1 rounded hover:bg-slate-100"
                      ><CalendarIcon className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject dialog */}
      {rejectOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setRejectOpen(null)}>
          <div className="bg-white rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Alle Vorschläge ablehnen</h3>
            <p className="text-sm text-slate-600 mb-4">Unser Team wird sich mit Ihnen in Verbindung setzen, um einen neuen Termin zu finden.</p>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Nachricht (optional)</label>
            <textarea rows="3" value={rejectMessage} onChange={(e) => setRejectMessage(e.target.value)} placeholder="z.B. Bitte Termine ab 15 Uhr vorschlagen..." data-testid="reject-message" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            <div className="flex gap-2 pt-4 justify-end">
              <button type="button" onClick={() => setRejectOpen(null)} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Abbrechen</button>
              <button type="button" onClick={rejectAllProposals} data-testid="confirm-reject-btn" className="bg-red-600 hover:bg-red-700 text-white font-medium rounded-md px-4 py-2 text-sm">Ablehnen senden</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default CustomerJobs;
